import { useContext, useState } from "react";
import { Icon, Loader, ViewContext } from "@/components/lib";
import { useForm } from "react-hook-form";
import axios from "axios";
import Styles from "@/css/mainStyles";

export default function PasswordModal({ setOpen }) {
  const viewContext = useContext<any>(ViewContext);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [textError, setTextError] = useState("");

  const changePasswordSubmit = async (data) => {
    setTextError("");
    if (data.newpassword !== data.confirmPassword) {
      setTextError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios({
        method: "put",
        url: "/api/user/password",
        data
      });
      setSent(true);
      viewContext.notification.show("Password changed", "success");
      setOpen(false);
    } catch (error) {
      console.error(error);
      viewContext.notification.show(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return sent ? (
    <div className="flex flex-col text-center justify-center">
      <Icon image="check-circle" size={50} className="mx-auto text-green-500" />
      <h2 className="text-lg md:text-2xl font-bold mb-2">
        Your Password Has Been Changed!
      </h2>
      <h3 className="text-sm md:text-lg mb-4">
        You can now log in using your new credentials
      </h3>
      <button onClick={() => setOpen(false)} className={Styles.cancelButton}>
        Close
      </button>
    </div>
  ) : (
    <form onSubmit={handleSubmit(changePasswordSubmit)}>
      <h2 className="text-lg md:text-2xl font-bold mb-2">Change Password</h2>
      <p className="text-sm md:text-lg text-gray-600 mb-4">
        Please enter your current password and create a new password to update
        your account security.
      </p>

      <div className="space-y-3">
        <div>
          <label className="text-sm lg:text-2xl text-black font-medium ml-2 mb-2">
            Enter your current password
          </label>
          <input
            type="password"
            className={Styles.input}
            {...register("oldpassword", { required: true })}
          />
          {errors.oldPassword && (
            <p className="text-xs md:text-lg text-red-500">
              Please enter your current password
            </p>
          )}
        </div>

        <div>
          <label className="text-sm lg:text-2xl text-black font-medium ml-2 mb-2">
            Create your new password
          </label>
          <input
            type="password"
            className={Styles.input}
            {...register("newpassword", { required: true })}
          />
          {errors.newPassword && (
            <p className="text-xs md:text-lg text-red-500">
              Please create a new password
            </p>
          )}
        </div>

        <div>
          <label className="text-sm lg:text-2xl text-black font-medium ml-2 mb-2">
            Re-enter your new password
          </label>
          <input
            type="password"
            className={Styles.input}
            {...register("confirmPassword", { required: true })}
          />
          {errors.confirmPassword && (
            <p className="text-xs md:text-lg text-red-500">
              Please re-enter your new password
            </p>
          )}
        </div>

        <p className="text-xs md:text-lg text-gray-500">
          Ensure your new password is at least 8 characters long, includes a mix
          of letters and numbers, and is case-sensitive.
        </p>
      </div>

      {textError && (
        <p className="text-xs md:text-lg text-red-500">{textError}</p>
      )}

      <div className="flex justify-between mt-4 gap-4">
        <button onClick={() => setOpen(false)} className={Styles.cancelButton}>
          Cancel
        </button>
        <button className={Styles.submitButton}>
          Confirm {loading && <Loader />}
        </button>
      </div>
    </form>
  );
}
